import { useTranslation } from 'react-i18next';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Typography } from 'app/components/ui';
import { AnchorPicker } from '../AnchorPicker';
import { NewExtensionFormComponent, NewExtensionFormComponentRef, NewExtensionFormValues } from '../NewExtensionModal/NewExtensionForm';
import { cleanLabels } from '../NewExtensionModal/NewExtensionModal';
import { useUpdateExtension, useTaxonomyLanguages, UUID, ExtensionConceptSummary, CreateExtensionSummand, useConcept } from 'entities/tag';
import { FieldPath } from 'react-hook-form';
import { api } from 'shared/api/client';

interface EditExtensionModalProps {
  show: boolean;
  onClose: () => void;
  documentSuperId: number;
  taxonomyId: UUID;
  extension: ExtensionConceptSummary;
}

async function resolveSummands(summands: NewExtensionFormValues['summands'], documentSuperId: number): Promise<CreateExtensionSummand[]> {
  if (!summands || summands.length === 0) return [];
  const resolved = await Promise.all(
    summands.map(async (s) => {
      const { data: concept } = await api.GET('/taxonomy/concepts', {
        params: { query: { name: s.conceptName, documentSuperId: String(documentSuperId) } },
      });
      if (!concept?.id) throw new Error(`Could not resolve summand concept: ${s.conceptName}`);
      const dimQualifiers = (s.dimQualifiers ?? []).filter((q) => !!q.axisConceptId && !!q.memberConceptId);
      return { conceptId: concept.id, weight: s.weight, dimQualifiers };
    })
  );
  return resolved;
}

export const EditExtensionModal = ({ show, onClose, documentSuperId, taxonomyId, extension }: EditExtensionModalProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const [view, setView] = useState<'form' | 'anchorPicker'>('form');
  const [anchorType, setAnchorType] = useState<'wider' | 'narrower' | 'summand' | null>(null);
  const [anchorSelected, setAnchorSelected] = useState<string | null>(null);
  const [summandPickIndex, setSummandPickIndex] = useState<number | null>(null);
  const [widerAnchorName, setWiderAnchorName] = useState<string | null>(extension.widerAnchor.name);
  const [narrowerAnchorName, setNarrowerAnchorName] = useState<string | null>(extension.narrowerAnchors.length > 0 ? extension.narrowerAnchors[0].name : null);
  const formRef = useRef<NewExtensionFormComponentRef>(null);

  useEffect(() => {
    if (show) {
      setView('form');
      setWiderAnchorName(extension.widerAnchor.name);
      setNarrowerAnchorName(extension.narrowerAnchors.length > 0 ? extension.narrowerAnchors[0].name : null);
    }
  }, [show, extension]);

  const updateExtension = useUpdateExtension();
  const { data: languages } = useTaxonomyLanguages(documentSuperId, taxonomyId);
  const { data: widerAnchorConcept } = useConcept(widerAnchorName, documentSuperId);
  const { data: narrowerAnchorConcept } = useConcept(narrowerAnchorName, documentSuperId);

  // Pre-fill form with the extension's current values.
  const defaultValues = useMemo<Partial<NewExtensionFormValues>>(() => {
    const ct = (extension.conceptType ?? {}) as { baseXbrliType?: string; balance?: string | null; periodType?: string };
    return {
      labels: extension.labels as NewExtensionFormValues['labels'],
      concept: extension.name.includes(':') ? extension.name.slice(extension.name.indexOf(':') + 1) : extension.name,
      description: extension.description ?? '',
      widerAnchor: extension.widerAnchor.name,
      narrowerAnchors: extension.narrowerAnchors[0]?.name ?? '',
      dataType: ct.baseXbrliType ?? 'monetaryItemType',
      balanceType: (ct.balance as 'debit' | 'credit' | null) ?? 'none',
      periodType: (ct.periodType as 'instant' | 'duration') ?? 'duration',
      kind: extension.kind,
      summands: extension.summands.map((s) => ({
        conceptName: s.name,
        weight: s.weight === -1 ? -1 : 1,
        dimQualifiers: s.dimQualifiers.map((q) => ({ axisConceptId: q.axisConceptId, memberConceptId: q.memberConceptId })),
      })),
    };
  }, [extension]);

  const openAnchorPicker = (type: 'wider' | 'narrower' | 'summand', selected: string | null, summandIndex?: number) => {
    setAnchorType(type);
    setAnchorSelected(selected);
    setSummandPickIndex(typeof summandIndex === 'number' ? summandIndex : null);
    setView('anchorPicker');
  };

  const handleAnchorPicked = (conceptName: string) => {
    if (anchorType === 'wider') {
      setWiderAnchorName(conceptName);
      formRef.current?.setValue('widerAnchor', conceptName);
    } else if (anchorType === 'narrower') {
      setNarrowerAnchorName(conceptName);
      formRef.current?.setValue('narrowerAnchors', conceptName);
    } else if (anchorType === 'summand' && summandPickIndex !== null) {
      formRef.current?.setValue(`summands.${summandPickIndex}.conceptName` as FieldPath<NewExtensionFormValues>, conceptName);
    }
    setView('form');
  };

  const handleSubmit = async (data: NewExtensionFormValues) => {
    const narrowerAnchorConceptIds = narrowerAnchorConcept?.id ? [narrowerAnchorConcept.id] : [];
    const summands = data.kind === 'SUB_TOTAL' ? await resolveSummands(data.summands ?? [], documentSuperId) : undefined;

    const balance: 'debit' | 'credit' | null = data.balanceType === 'debit' || data.balanceType === 'credit' ? data.balanceType : null;
    const periodType: 'instant' | 'duration' = data.periodType === 'instant' ? 'instant' : 'duration';

    await updateExtension.mutateAsync({
      conceptId: extension.id,
      documentSuperId,
      taxonomyId,
      name: data.concept,
      description: data.description ?? null,
      labels: cleanLabels(data.labels),
      widerAnchorConceptId: widerAnchorConcept?.id,
      narrowerAnchorConceptIds,
      conceptType: { baseXbrliType: data.dataType, balance, periodType },
      summands,
    });
    onClose();
  };

  const readyLanguages = languages && languages.length > 0 ? languages : undefined;
  // Re-key on `extension.id` so the form remounts (and picks up new
  // defaults) whenever the user navigates from one extension to another.
  const formKey = `${show ? 'open' : 'closed'}-${extension.id}-${(languages ?? []).join(',')}`;

  return (
    <BootstrapModal show={show} onHide={onClose} contentClassName="new-extension__modal" centered>
      <BootstrapModal.Header className="new-extension__modal--header">
        <BootstrapModal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {t('tag:extension.edit.title')}
          </Typography>
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="new-extension__modal--body">
        <div style={{ display: view === 'form' ? 'block' : 'none' }}>
          {readyLanguages && (
            <NewExtensionFormComponent
              key={formKey}
              ref={formRef}
              languages={readyLanguages}
              onOpenAnchorPicker={openAnchorPicker}
              onCancel={onClose}
              onSubmit={handleSubmit}
              widerAnchorSeed={extension.widerAnchor.name}
              documentSuperId={documentSuperId}
              defaultValues={defaultValues}
              editMode
            />
          )}
        </div>

        {view === 'anchorPicker' && anchorType && (
          <AnchorPicker
            anchorKind={anchorType === 'summand' ? 'narrower' : anchorType}
            documentSuperId={documentSuperId}
            selected={anchorSelected}
            onSelect={({ conceptName }) => handleAnchorPicked(conceptName)}
            onClose={() => setView('form')}
          />
        )}
      </BootstrapModal.Body>
      {view === 'form' && (
        <BootstrapModal.Footer className="new-extension__modal--footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common:cancel')}
          </button>
          <button type="submit" form="new-extension-form" className="btn btn-primary">
            {t('common:save')}
          </button>
        </BootstrapModal.Footer>
      )}
    </BootstrapModal>
  );
};
